import React, { ChangeEvent, useState } from 'react';
import { Button, ButtonSize, ButtonStyle, ButtonType } from 'common/button';
import { goToPage, RouteName } from 'routes';
import { Notification } from 'common/notification';
import { UserController } from 'networking/controllers/user-controller';
import { Input } from 'common/input';
import { ChevronRightIcon } from 'assets/icons';
import { Spinner } from 'common/spinner';
import {
  classnames,
  isEmailValid,
  noEmptyFields,
  noErrors,
} from 'helpers/utils';
import { captureEvent } from 'posthog';
import { trackCustomEvent } from 'helpers/analytics';
import styles from './forgot-password-step.module.scss';

const mandatoryFields: MandatoryFieldsType = {
  email: '',
};

type ForgotPasswordStep1Props = {
  notifSuccess: () => void;
  email: string;
  setEmail: (email: string) => void;
};

const ForgotPasswordStep1: React.FC<ForgotPasswordStep1Props> = ({
  notifSuccess,
  email,
  setEmail,
}) => {
  const [errors, setErrors] = useState<MandatoryFieldsType>(mandatoryFields);
  const [showError, setShowError] = useState(false);
  const [fetching, setFetching] = useState(false);

  const validateEmail = (value: string) => {
    let emailMessage = '';

    emailMessage = value.trim() === '' ? "email shouldn't be empty" : '';
    setErrors((prevState) => ({
      ...prevState,
      email: emailMessage.toUpperCase(),
    }));

    if (emailMessage) return;

    emailMessage = !isEmailValid(value) ? 'invalid email' : '';
    setErrors((prevState) => ({
      ...prevState,
      email: emailMessage.toUpperCase(),
    }));
  };

  const handleSubmit = async () => {
    validateEmail(email);

    if (!noErrors(errors)) {
      return;
    }

    setFetching(true);

    captureEvent('sendPasswordResetEmail');
    trackCustomEvent('sendPasswordResetEmail');

    try {
      await UserController.forgotPassword(email);
      notifSuccess();
    } catch (e) {
      setShowError(true);
    } finally {
      setFetching(false);
    }
  };

  const showErrorNotification = () => (
    <Notification handleClose={() => setShowError(false)} />
  );

  const showContentView = () => (
    <>
      <div className={classnames(styles.footerForgotReset)}>
        <span className={styles.chevronIcon}>
          <ChevronRightIcon />
        </span>

        <Button
          buttonStyle={ButtonStyle.PrimaryGhost}
          buttonSize={ButtonSize.Small}
          onClick={() => goToPage(RouteName.SignIn)}
        >
          Back to Login
        </Button>
      </div>
      <div className={styles.subtitleContent}>
        <div className={classnames('text__title1__gray800', 'mt-15')}>
          Reset your password
        </div>
        <div className="text__body__regular__medium__gray700">
          Enter the email associated with your account, and we will send you
          instructions to reset your password.
        </div>
      </div>

      <Input
        id="email"
        name="email"
        label="Email"
        value={email}
        type="email"
        errorMessage={errors.email}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          setEmail(e.target.value)
        }
        onBlur={(e) => validateEmail(e.target.value)}
        innerClassName={styles.input}
      />
      <Button
        className={classnames(
          styles.sendButton,
          fetching ? 'noClick fixSizeFetching' : '',
        )}
        onClick={() => handleSubmit()}
        buttonSize={ButtonSize.Medium}
        buttonType={ButtonType.Submit}
        disabled={!noEmptyFields({ email })}
      >
        {fetching ? (
          <Spinner color="white" className="spinner-inside-button" />
        ) : (
          <> Send instructions </>
        )}
      </Button>
    </>
  );

  return (
    <>
      {showError && showErrorNotification()}
      {showContentView()}
    </>
  );
};

export { ForgotPasswordStep1 };
